import React, { useState, useEffect, useCallback } from 'react';
import { adminManagementAPI, adminAuthAPI, subRoleAPI } from '../../../../services';
import { adminHasPermission, getCurrentAdmin, isAdminAuth, adminLogout } from '../../../../services/auth/authManager';
import { clearCachedSubRolePermissions } from '../../../../services/auth/permissionMiddleware';
import { useNotification } from '../../../../services/notificationService';
import { handlePermissionError } from '../../../../services/utils/permissionErrorHandler';
import './AdminManagement.css';

const ROLES = {
  super_admin: { label: 'Super Admin', color: '#dc3545', icon: 'fa-crown' },
  admin: { label: 'Admin', color: '#fd7e14', icon: 'fa-shield-alt' },
  moderator: { label: 'Moderator', color: '#0d6efd', icon: 'fa-balance-scale' },
  viewer: { label: 'Viewer', color: '#6c757d', icon: 'fa-eye' }
};

const AdminManagement = () => {
  const { success, error: notifyError } = useNotification();
  const [admins, setAdmins] = useState([]);
  const [subRoles, setSubRoles] = useState([]);
  const [adminSubRoles, setAdminSubRoles] = useState({});
  const [loading, setLoading] = useState(false);
  const [pageError, setPageError] = useState(null);
  const [currentAdmin, setCurrentAdmin] = useState(null);

  // Panel state - replaces most modals
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [panelMode, setPanelMode] = useState(null); // 'detail' | 'edit' | 'roles'

  // Modals (kept for self-service and create)
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSessionsModal, setShowSessionsModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [sessions, setSessions] = useState([]);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // Forms
  const [formData, setFormData] = useState({
    username: '', email: '', password: '', confirmPassword: '',
    role: 'admin', firstName: '', lastName: '', isActive: true
  });
  const [passwordForm, setPasswordForm] = useState({ newPassword: '', confirmNewPassword: '' });
  const [changeOwnPasswordForm, setChangeOwnPasswordForm] = useState({
    currentPassword: '', newPassword: '', confirmNewPassword: ''
  });
  const [selectedSubRoleIds, setSelectedSubRoleIds] = useState([]);

  useEffect(() => {
    const admin = getCurrentAdmin();
    setCurrentAdmin(admin);
    if (!isAdminAuth()) { setPageError('Please log in to access this page'); return; }
    Promise.all([loadAdmins(), loadSessions(), loadAllSubRoles()]);
  }, []);

  const loadAdmins = useCallback(async () => {
    if (!adminHasPermission('admins.read')) { setPageError('You do not have permission to view admins'); return; }
    setLoading(true);
    try {
      const result = await adminManagementAPI.getAllAdmins();
      setAdmins(result.admins || []);
    } catch { setPageError('Failed to load admins.'); }
    finally { setLoading(false); }
  }, []);

  const loadSessions = async () => {
    try { const r = await adminAuthAPI.getSessions(); setSessions(r.sessions || []); } catch {}
  };

  const loadAllSubRoles = async () => {
    try { const r = await subRoleAPI.getAllSubRoles(); setSubRoles(r.data || []); } catch {}
  };

  const loadAdminSubRoles = useCallback(async (adminId) => {
    try {
      const r = await subRoleAPI.getAdminSubRoles(adminId);
      setAdminSubRoles(prev => ({ ...prev, [adminId]: r.data || [] }));
      return r.data || [];
    } catch { return []; }
  }, []);

  useEffect(() => {
    admins.forEach(a => { loadAdminSubRoles(a.id || a._id); });
  }, [admins, loadAdminSubRoles]);

  // -- Panel actions --
  const openPanel = (admin, mode = 'detail') => {
    const adminId = admin.id || admin._id;
    setSelectedAdmin({ ...admin, _effectiveId: adminId });
    setPanelMode(mode);
    loadAdminSubRoles(adminId);
  };
  const closePanel = () => { setSelectedAdmin(null); setPanelMode(null); setPasswordForm({ newPassword: '', confirmNewPassword: '' }); };
  const swapToMode = (mode) => setPanelMode(mode);

  // Create admin
  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) { notifyError('Passwords do not match'); return; }
    setLoading(true);
    try {
      await adminManagementAPI.createAdmin(formData);
      success('Admin created successfully');
      setShowCreateModal(false); resetCreateForm(); loadAdmins();
    } catch (err) {
      const d = err.response?.data;
      notifyError((d?.data?.errors || []).map(e => e.msg).join(', ') || d?.message || 'Failed to create admin');
    } finally { setLoading(false); }
  };

  // Update admin (from panel)
  const handleUpdateAdmin = async () => {
    if (!selectedAdmin) return;
    setLoading(true);
    try {
      await adminManagementAPI.updateAdmin(selectedAdmin._effectiveId, {
        username: selectedAdmin.username,
        firstName: selectedAdmin.profile?.firstName || '',
        lastName: selectedAdmin.profile?.lastName || '',
        isActive: selectedAdmin.isActive
      });
      success('Admin updated successfully');
      closePanel(); loadAdmins();
    } catch (err) { notifyError(err.response?.data?.message || 'Failed to update admin'); }
    finally { setLoading(false); }
  };

  // Change role
  const handleChangeRole = async (adminId, newRole) => {
    if (!adminHasPermission('system.super')) { notifyError('Permission denied'); return; }
    try {
      await adminManagementAPI.changeAdminRole(adminId, newRole);
      success('Role updated');
      loadAdmins();
    } catch (err) { notifyError(err.response?.data?.message || 'Failed to change role'); }
  };

  // Reset password
  const handleResetPassword = async () => {
    if (!selectedAdmin || !passwordForm.newPassword) { notifyError('Enter a new password'); return; }
    if (passwordForm.newPassword !== passwordForm.confirmNewPassword) { notifyError('Passwords do not match'); return; }
    setLoading(true);
    try {
      await adminManagementAPI.resetAdminPassword(selectedAdmin._effectiveId, passwordForm.newPassword);
      success('Password reset successfully');
      closePanel();
    } catch (err) { notifyError(err.response?.data?.message || 'Failed to reset password'); }
    finally { setLoading(false); }
  };

  // Delete admin
  const handleDeleteAdmin = async (adminId) => {
    const admin = admins.find(a => (a.id || a._id) === adminId);
    if (admin?.isSuperAdmin || admin?.role === 'super_admin') { notifyError('Cannot delete super admin'); return; }
    if ((adminId) === (currentAdmin?.id || currentAdmin?._id)) { notifyError('Cannot delete your own account'); return; }
    if (!window.confirm('Delete this admin permanently?')) return;
    setLoading(true);
    try {
      await adminManagementAPI.deleteAdmin(adminId);
      success('Admin deleted');
      closePanel(); loadAdmins();
    } catch (err) { notifyError(err.response?.data?.message || 'Failed to delete admin'); }
    finally { setLoading(false); }
  };

  // Assign sub-roles
  const handleAssignSubRoles = async () => {
    if (!selectedAdmin) return;
    setLoading(true);
    try {
      await subRoleAPI.assignSubRolesToAdmin(selectedAdmin._effectiveId, selectedSubRoleIds);
      clearCachedSubRolePermissions(selectedAdmin._effectiveId);
      success('Sub-roles assigned');
      loadAdminSubRoles(selectedAdmin._effectiveId);
    } catch (err) { handlePermissionError(err); notifyError(err.response?.data?.message || 'Failed to assign sub-roles'); }
    finally { setLoading(false); }
  };

  // Own password change
  const handleChangeOwnPassword = async (e) => {
    e.preventDefault();
    if (changeOwnPasswordForm.newPassword !== changeOwnPasswordForm.confirmNewPassword) { notifyError('New passwords do not match'); return; }
    setLoading(true);
    try {
      await adminAuthAPI.changePassword(changeOwnPasswordForm);
      success('Password changed. Logging out...');
      setShowChangePasswordModal(false);
      setTimeout(() => adminLogout(), 2000);
    } catch (err) { notifyError(err.response?.data?.message || 'Failed'); }
    finally { setLoading(false); }
  };

  const handleLogoutAllOthers = async () => {
    try { const r = await adminAuthAPI.logoutAllOthers(); success(r.message); loadSessions(); }
    catch (err) { notifyError(err.response?.data?.message || 'Failed'); }
  };

  const resetCreateForm = () => {
    setFormData({ username: '', email: '', password: '', confirmPassword: '', role: 'admin', firstName: '', lastName: '', isActive: true });
  };

  const formatDate = (s) => s ? new Date(s).toLocaleString() : 'Never';

  const canManageAdmin = (admin) => {
    if (!currentAdmin) return false;
    if ((currentAdmin.id || currentAdmin._id) === (admin.id || admin._id)) return false;
    const isSuper = currentAdmin.isSuperAdmin || currentAdmin.role === 'super_admin';
    if (isSuper) return true;
    if (admin.isSuperAdmin || admin.role === 'super_admin') return false;
    return adminHasPermission('admins.update');
  };

  const canChangeRoles = () => adminHasPermission('system.super') && (currentAdmin?.isSuperAdmin || currentAdmin?.role === 'super_admin');

  const filteredAdmins = admins.filter(a => {
    const s = searchTerm.toLowerCase();
    const match = !s || a.username.toLowerCase().includes(s) || a.email.toLowerCase().includes(s)
      || (a.profile?.firstName || '').toLowerCase().includes(s)
      || (a.profile?.lastName || '').toLowerCase().includes(s);
    const roleMatch = roleFilter === 'all' || a.role === roleFilter;
    const statusMatch = statusFilter === 'all' || (statusFilter === 'active' ? a.isActive : !a.isActive);
    return match && roleMatch && statusMatch;
  });

  const stats = {
    total: admins.length,
    active: admins.filter(a => a.isActive).length,
    superAdmins: admins.filter(a => a.isSuperAdmin || a.role === 'super_admin').length
  };

  const assignedSubRolesForSelected = selectedAdmin
    ? (adminSubRoles[selectedAdmin._effectiveId] || [])
    : [];

  if (pageError && !admins.length) {
    return (
      <div className="am-container">
        <div className="am-empty-state">
          <div className="am-empty-icon"><i className="fas fa-exclamation-triangle"></i></div>
          <h3>Access Denied</h3>
          <p>{pageError}</p>
          <button className="am-btn am-btn-primary" onClick={loadAdmins}><i className="fas fa-sync"></i> Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="am-container">
      {/* Header */}
      <div className="am-header">
        <div className="am-header-left">
          <div className="am-header-icon"><i className="fas fa-users"></i></div>
          <div>
            <h1>Admin Management</h1>
            <p>{stats.total} admins &middot; {stats.active} active &middot; {stats.superAdmins} super</p>
          </div>
        </div>
        <div className="am-header-actions">
          <button className="am-btn am-btn-ghost" onClick={loadAdmins}><i className="fas fa-sync"></i> Refresh</button>
          <button className="am-btn am-btn-ghost" onClick={() => setShowSessionsModal(true)}>
            <i className="fas fa-lock"></i> Sessions
          </button>
          <button className="am-btn am-btn-ghost" onClick={() => setShowChangePasswordModal(true)}>
            <i className="fas fa-key"></i> Change Password
          </button>
          {adminHasPermission('admins.create') && (
            <button className="am-btn am-btn-primary" onClick={() => { resetCreateForm(); setShowCreateModal(true); }}>
              <i className="fas fa-plus"></i> Add Admin
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="am-filters">
        <div className="am-search">
          <i className="fas fa-search"></i>
          <input placeholder="Search by name, email..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
        </div>
        <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)}>
          <option value="all">All Roles</option>
          <option value="super_admin">Super Admin</option>
          <option value="admin">Admin</option>
          <option value="moderator">Moderator</option>
          <option value="viewer">Viewer</option>
        </select>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {/* Main layout: Table + Panel */}
      <div className="am-layout">
        {/* Left: Admin List */}
        <div className={`am-list-panel ${selectedAdmin ? 'has-panel' : ''}`}>
          {loading ? (
            <div className="am-loading"><div className="am-spinner"></div><p>Loading...</p></div>
          ) : filteredAdmins.length === 0 ? (
            <div className="am-empty-state">
              <i className="fas fa-user-slash am-empty-icon"></i>
              <h3>No admins found</h3>
              <p>{searchTerm || roleFilter !== 'all' || statusFilter !== 'all' ? 'Try adjusting filters.' : 'No admins in the system.'}</p>
            </div>
          ) : (
            <div className="am-table-wrapper">
              <div className="am-table-header">
                <span className="am-th am-th-name">Admin</span>
                <span className="am-th am-th-role">Role</span>
                <span className="am-th am-th-subroles">Sub-Roles</span>
                <span className="am-th am-th-status">Status</span>
                <span className="am-th am-th-lastlogin">Last Login</span>
                <span className="am-th am-th-actions"></span>
              </div>
              {filteredAdmins.map(admin => {
                const aid = admin.id || admin._id;
                const roleInfo = ROLES[admin.role] || ROLES.viewer;
                const assigned = adminSubRoles[aid] || [];
                const isSuper = admin.isSuperAdmin || admin.role === 'super_admin';
                const isSelected = selectedAdmin && selectedAdmin._effectiveId === aid;
                return (
                  <div key={aid}
                    className={`am-tr ${isSelected ? 'selected' : ''} ${!admin.isActive ? 'inactive' : ''} ${isSuper ? 'super' : ''}`}
                    onClick={() => openPanel(admin)}
                  >
                    <span className="am-td am-td-name">
                      <span className="am-avatar-sm">{(admin.profile?.firstName?.[0] || admin.username[0]).toUpperCase()}</span>
                      <span>
                        <strong>{admin.profile?.firstName} {admin.profile?.lastName}</strong>
                        <small>@{admin.username}</small>
                      </span>
                      {isSuper && <span className="am-badge am-badge-super"><i className="fas fa-crown"></i></span>}
                    </span>
                    <span className="am-td am-td-role">
                      <span className="am-role-tag" style={{ background: roleInfo.color + '18', color: roleInfo.color, borderColor: roleInfo.color + '40' }}>
                        <i className={`fas ${roleInfo.icon}`}></i> {roleInfo.label}
                      </span>
                    </span>
                    <span className="am-td am-td-subroles">
                      {assigned.length > 0 ? (
                        <span className="am-subrole-chips">
                          {assigned.slice(0, 3).map(sr => (
                            <span key={sr._id || sr.name} className="am-chip" title={sr.displayName}>{sr.displayName}</span>
                          ))}
                          {assigned.length > 3 && <span className="am-chip am-chip-more">+{assigned.length - 3}</span>}
                        </span>
                      ) : <span className="am-muted">None</span>}
                    </span>
                    <span className="am-td am-td-status">
                      <span className={`am-status-dot ${admin.isActive ? 'active' : 'inactive'}`}></span>
                      {admin.isActive ? 'Active' : 'Inactive'}
                    </span>
                    <span className="am-td am-td-lastlogin">{formatDate(admin.lastLogin)}</span>
                    <span className="am-td am-td-actions" onClick={e => e.stopPropagation()}>
                      {canManageAdmin(admin) && (
                        <>
                          <button className="am-icon-btn" title="Edit" onClick={() => openPanel(admin, 'edit')}><i className="fas fa-edit"></i></button>
                          <button className="am-icon-btn am-icon-danger" title="Delete" onClick={() => handleDeleteAdmin(aid)}><i className="fas fa-trash"></i></button>
                        </>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Right: Detail/Edit Panel */}
        {selectedAdmin && (
          <div className={`am-detail-panel ${panelMode}`}>
            <div className="am-panel-header">
              <button className="am-panel-back" onClick={closePanel}><i className="fas fa-times"></i></button>
              <div className="am-panel-tabs">
                <button className={panelMode === 'detail' ? 'active' : ''} onClick={() => swapToMode('detail')}>Details</button>
                {canManageAdmin(selectedAdmin) && (
                  <button className={panelMode === 'edit' ? 'active' : ''} onClick={() => swapToMode('edit')}>Edit</button>
                )}
                {adminHasPermission('system.super') && (
                  <button className={panelMode === 'roles' ? 'active' : ''} onClick={() => swapToMode('roles')}>Sub-Roles</button>
                )}
              </div>
            </div>

            <div className="am-panel-body">
              {/* Detail Mode */}
              {panelMode === 'detail' && (
                <div className="am-detail-view">
                  <div className="am-profile-header">
                    <div className="am-avatar-lg">{(selectedAdmin.profile?.firstName?.[0] || selectedAdmin.username[0]).toUpperCase()}</div>
                    <div className="am-profile-meta">
                      <h3>
                        {selectedAdmin.profile?.firstName} {selectedAdmin.profile?.lastName}
                        {(selectedAdmin.isSuperAdmin || selectedAdmin.role === 'super_admin') && (
                          <span className="am-super-badge"><i className="fas fa-crown"></i> Super Admin</span>
                        )}
                      </h3>
                      <div className="am-profile-row">
                        <span className="am-username">@{selectedAdmin.username}</span>
                        {!(selectedAdmin.isSuperAdmin || selectedAdmin.role === 'super_admin') && (
                          <span className="am-role-tag" style={{ background: (ROLES[selectedAdmin.role] || ROLES.viewer).color + '18', color: (ROLES[selectedAdmin.role] || ROLES.viewer).color }}>
                            <i className={`fas ${(ROLES[selectedAdmin.role] || ROLES.viewer).icon}`}></i> {(ROLES[selectedAdmin.role] || ROLES.viewer).label}
                          </span>
                        )}
                      </div>
                    </div>
                    {canChangeRoles() && !(selectedAdmin.isSuperAdmin || selectedAdmin.role === 'super_admin') && (
                      <select className="am-role-select-sm"
                        value={selectedAdmin.role}
                        onChange={e => handleChangeRole(selectedAdmin._effectiveId, e.target.value)}
                      >
                        <option value="admin">Admin</option>
                        <option value="moderator">Moderator</option>
                        <option value="viewer">Viewer</option>
                      </select>
                    )}
                  </div>

                  <div className="am-detail-grid">
                    <div className="am-detail-item"><label>Email</label><span>{selectedAdmin.email}</span></div>
                    <div className="am-detail-item"><label>Status</label><span className={`am-status-badge ${selectedAdmin.isActive ? 'active' : 'inactive'}`}>{selectedAdmin.isActive ? 'Active' : 'Inactive'}</span></div>
                    <div className="am-detail-item"><label>Last Login</label><span>{formatDate(selectedAdmin.lastLogin)}</span></div>
                    <div className="am-detail-item"><label>Created</label><span>{formatDate(selectedAdmin.createdAt)}</span></div>
                    <div className="am-detail-item"><label>Updated</label><span>{formatDate(selectedAdmin.updatedAt)}</span></div>
                    <div className="am-detail-item"><label>Admin ID</label><span className="am-mono">{selectedAdmin._effectiveId}</span></div>
                  </div>

                  <div className="am-section">
                    <h4 className="am-section-title">Assigned Sub-Roles ({assignedSubRolesForSelected.length})</h4>
                    {assignedSubRolesForSelected.length === 0 ? (
                      <p className="am-muted">No sub-roles assigned.</p>
                    ) : (
                      <div className="am-subrole-chips">
                        {assignedSubRolesForSelected.map(sr => (
                          <span key={sr._id || sr.name} className="am-chip">
                            <i className="fas fa-shield-alt"></i> {sr.displayName}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="am-panel-actions">
                    {canManageAdmin(selectedAdmin) && (
                      <button className="am-btn am-btn-primary" onClick={() => swapToMode('edit')}><i className="fas fa-edit"></i> Edit Admin</button>
                    )}
                    {adminHasPermission('system.super') && (
                      <>
                        <button className="am-btn am-btn-ghost" onClick={() => swapToMode('roles')}><i className="fas fa-user-shield"></i> Manage Sub-Roles</button>
                        <button className="am-btn am-btn-warning" onClick={() => { setPasswordForm({ newPassword: '', confirmNewPassword: '' }); swapToMode('edit'); }}>
                          <i className="fas fa-key"></i> Reset Password
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Edit Mode */}
              {panelMode === 'edit' && (
                <div className="am-edit-view">
                  <div className="am-form-section">
                    <h4 className="am-section-title">Edit Profile</h4>
                    <div className="am-form-row">
                      <div className="am-form-group">
                        <label>First Name</label>
                        <input type="text" value={selectedAdmin.profile?.firstName || ''}
                          onChange={e => setSelectedAdmin(prev => ({ ...prev, profile: { ...prev.profile, firstName: e.target.value } }))} />
                      </div>
                      <div className="am-form-group">
                        <label>Last Name</label>
                        <input type="text" value={selectedAdmin.profile?.lastName || ''}
                          onChange={e => setSelectedAdmin(prev => ({ ...prev, profile: { ...prev.profile, lastName: e.target.value } }))} />
                      </div>
                    </div>
                    <div className="am-form-group">
                      <label>Email & Username</label>
                      <div className="am-form-row">
                        <input type="email" value={selectedAdmin.email} disabled />
                        <input type="text" value={selectedAdmin.username} disabled />
                      </div>
                    </div>
                    <div className="am-form-group">
                      <label className="am-checkbox">
                        <input type="checkbox" checked={selectedAdmin.isActive}
                          onChange={e => setSelectedAdmin(prev => ({ ...prev, isActive: e.target.checked }))} />
                        <span>Active Account</span>
                      </label>
                    </div>
                    <button className="am-btn am-btn-primary" onClick={handleUpdateAdmin} disabled={loading}>
                      {loading ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>

                  {/* Reset Password Section */}
                  {adminHasPermission('system.super') && (
                    <div className="am-form-section am-section-warning">
                      <h4 className="am-section-title"><i className="fas fa-key"></i> Reset Password for {selectedAdmin.username}</h4>
                      <p className="am-muted">The admin will be prompted to set a new password on next login.</p>
                      <div className="am-form-row">
                        <div className="am-form-group">
                          <label>New Password</label>
                          <input type="password" value={passwordForm.newPassword}
                            onChange={e => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                            placeholder="Min 8 chars" />
                        </div>
                        <div className="am-form-group">
                          <label>Confirm Password</label>
                          <input type="password" value={passwordForm.confirmNewPassword}
                            onChange={e => setPasswordForm(prev => ({ ...prev, confirmNewPassword: e.target.value }))}
                            placeholder="Re-enter password" />
                        </div>
                      </div>
                      <button className="am-btn am-btn-warning" onClick={handleResetPassword} disabled={loading}>
                        <i className="fas fa-key"></i> Reset Password
                      </button>
                    </div>
                  )}

                  {/* Danger zone */}
                  {canManageAdmin(selectedAdmin) && !(selectedAdmin.isSuperAdmin || selectedAdmin.role === 'super_admin') && (
                    <div className="am-form-section am-section-danger">
                      <h4 className="am-section-title"><i className="fas fa-exclamation-triangle"></i> Danger Zone</h4>
                      <p className="am-muted">Permanently delete this admin account and all associated data.</p>
                      <button className="am-btn am-btn-danger" onClick={() => handleDeleteAdmin(selectedAdmin._effectiveId)} disabled={loading}>
                        <i className="fas fa-trash"></i> Delete Admin
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Sub-Roles Mode */}
              {panelMode === 'roles' && (
                <div className="am-roles-view">
                  <h4 className="am-section-title">Assign Sub-Roles to {selectedAdmin.username}</h4>
                  <p className="am-muted">
                    Main role: <strong>{(ROLES[selectedAdmin.role] || ROLES.viewer).label}</strong>.
                    Only sub-roles matching this main role are shown.
                  </p>
                  {(() => {
                    const available = subRoles.filter(sr =>
                      sr.mainRole === selectedAdmin.role && sr.isActive
                    );
                    const currentIds = (adminSubRoles[selectedAdmin._effectiveId] || []).map(sr => sr._id || sr.name);

                    // Initialize selectedSubRoleIds from current assignments
                    if (selectedSubRoleIds.length === 0 && currentIds.length > 0) {
                      setTimeout(() => setSelectedSubRoleIds(currentIds), 0);
                    }

                    return available.length === 0 ? (
                      <p className="am-muted">No sub-roles available for the "{selectedAdmin.role}" role. Create sub-roles first.</p>
                    ) : (
                      <>
                        <div className="am-subrole-assign-list">
                          {available.map(sr => {
                            const sid = sr._id || sr.name;
                            const isChecked = selectedSubRoleIds.includes(sid);
                            return (
                              <label key={sid} className={`am-assign-item ${isChecked ? 'checked' : ''}`}>
                                <input type="checkbox" checked={isChecked}
                                  onChange={() => {
                                    setSelectedSubRoleIds(prev =>
                                      prev.includes(sid) ? prev.filter(id => id !== sid) : [...prev, sid]
                                    );
                                  }} />
                                <div>
                                  <strong>{sr.displayName}</strong>
                                  <small>{sr.description}</small>
                                </div>
                                <span className="am-priority-badge">P{sr.priority}</span>
                              </label>
                            );
                          })}
                        </div>
                        <div className="am-panel-actions">
                          <button className="am-btn am-btn-primary" onClick={handleAssignSubRoles} disabled={loading}>
                            {loading ? 'Saving...' : 'Save Assignments'}
                          </button>
                          <button className="am-btn am-btn-ghost" onClick={() => setSelectedSubRoleIds(currentIds)}>
                            Reset
                          </button>
                        </div>
                      </>
                    );
                  })()}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Create Admin Modal */}
      {showCreateModal && (
        <div className="am-modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="am-modal" onClick={e => e.stopPropagation()}>
            <div className="am-modal-header">
              <h2><i className="fas fa-user-plus"></i> Create Admin</h2>
              <button className="am-close-btn" onClick={() => setShowCreateModal(false)}><i className="fas fa-times"></i></button>
            </div>
            <form onSubmit={handleCreateAdmin}>
              <div className="am-modal-body">
                <div className="am-form-row">
                  <div className="am-form-group"><label>Username *</label><input type="text" value={formData.username} onChange={e => setFormData(f => ({ ...f, username: e.target.value }))} required /></div>
                  <div className="am-form-group"><label>Email *</label><input type="email" value={formData.email} onChange={e => setFormData(f => ({ ...f, email: e.target.value }))} required /></div>
                </div>
                <div className="am-form-row">
                  <div className="am-form-group"><label>Password *</label><input type="password" value={formData.password} onChange={e => setFormData(f => ({ ...f, password: e.target.value }))} required placeholder="Min 8 chars, upper, lower, number, special" /></div>
                  <div className="am-form-group"><label>Confirm *</label><input type="password" value={formData.confirmPassword} onChange={e => setFormData(f => ({ ...f, confirmPassword: e.target.value }))} required /></div>
                </div>
                <div className="am-form-row">
                  <div className="am-form-group"><label>First Name</label><input type="text" value={formData.firstName} onChange={e => setFormData(f => ({ ...f, firstName: e.target.value }))} /></div>
                  <div className="am-form-group"><label>Last Name</label><input type="text" value={formData.lastName} onChange={e => setFormData(f => ({ ...f, lastName: e.target.value }))} /></div>
                </div>
                <div className="am-form-group">
                  <label>Role</label>
                  <select value={formData.role} onChange={e => setFormData(f => ({ ...f, role: e.target.value }))}>
                    {adminHasPermission('system.super') && <option value="super_admin">Super Admin</option>}
                    <option value="admin">Admin</option>
                    <option value="moderator">Moderator</option>
                    <option value="viewer">Viewer</option>
                  </select>
                </div>
              </div>
              <div className="am-modal-footer">
                <button type="button" className="am-btn am-btn-ghost" onClick={() => setShowCreateModal(false)}>Cancel</button>
                <button type="submit" className="am-btn am-btn-primary" disabled={loading}>{loading ? 'Creating...' : 'Create Admin'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Sessions Modal */}
      {showSessionsModal && (
        <div className="am-modal-overlay" onClick={() => setShowSessionsModal(false)}>
          <div className="am-modal am-modal-lg" onClick={e => e.stopPropagation()}>
            <div className="am-modal-header">
              <h2><i className="fas fa-lock"></i> Active Sessions ({sessions.length})</h2>
              <button className="am-close-btn" onClick={() => setShowSessionsModal(false)}><i className="fas fa-times"></i></button>
            </div>
            <div className="am-modal-body">
              {sessions.length === 0 ? (
                <p className="am-muted">No active sessions.</p>
              ) : (
                <div className="am-sessions-list">
                  {sessions.map(s => (
                    <div key={s.id} className={`am-session-card ${s.isCurrent ? 'current' : ''}`}>
                      <div className="am-session-top">
                        <span>{s.deviceName || 'Unknown device'}</span>
                        {s.isCurrent && <span className="am-badge am-badge-current">Current</span>}
                      </div>
                      <div className="am-session-info">
                        <span>IP: {s.ipAddress}</span>
                        <span>Last: {formatDate(s.lastActivity)}</span>
                        <span>Expires: {formatDate(s.expiresAt)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="am-modal-footer">
              <button className="am-btn am-btn-ghost" onClick={() => setShowSessionsModal(false)}>Close</button>
              {sessions.some(s => !s.isCurrent) && (
                <button className="am-btn am-btn-warning" onClick={handleLogoutAllOthers}>
                  <i className="fas fa-sign-out-alt"></i> Logout All Others
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Change Own Password Modal */}
      {showChangePasswordModal && (
        <div className="am-modal-overlay" onClick={() => setShowChangePasswordModal(false)}>
          <div className="am-modal" onClick={e => e.stopPropagation()}>
            <div className="am-modal-header">
              <h2><i className="fas fa-key"></i> Change Your Password</h2>
              <button className="am-close-btn" onClick={() => setShowChangePasswordModal(false)}><i className="fas fa-times"></i></button>
            </div>
            <form onSubmit={handleChangeOwnPassword}>
              <div className="am-modal-body">
                <div className="am-form-group">
                  <label>Current Password *</label>
                  <input type="password" value={changeOwnPasswordForm.currentPassword}
                    onChange={e => setChangeOwnPasswordForm(f => ({ ...f, currentPassword: e.target.value }))} required />
                </div>
                <div className="am-form-group">
                  <label>New Password *</label>
                  <input type="password" value={changeOwnPasswordForm.newPassword}
                    onChange={e => setChangeOwnPasswordForm(f => ({ ...f, newPassword: e.target.value }))} required />
                </div>
                <div className="am-form-group">
                  <label>Confirm New Password *</label>
                  <input type="password" value={changeOwnPasswordForm.confirmNewPassword}
                    onChange={e => setChangeOwnPasswordForm(f => ({ ...f, confirmNewPassword: e.target.value }))} required />
                </div>
                <p className="am-muted"><i className="fas fa-info-circle"></i> You will be logged out after changing your password.</p>
              </div>
              <div className="am-modal-footer">
                <button type="button" className="am-btn am-btn-ghost" onClick={() => setShowChangePasswordModal(false)}>Cancel</button>
                <button type="submit" className="am-btn am-btn-warning" disabled={loading}>{loading ? 'Changing...' : 'Change Password'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminManagement;
